import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs } from '@/components/common/Tabs';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { useNopLive } from './useNopLive';
import { NopFilterBar, defaultNopFilters, toNopFilters, type NopFilterState } from './NopFilterBar';
import { NopSectionTable } from './NopSectionTable';
import { VolumeMatchingTable } from './VolumeMatchingTable';
import { EodSwapTab } from './EodSwapTab';
import { NopThresholdsPanel } from './NopThresholdsPanel';
import { ConsolidatedExposurePermissions as P } from './permissions';
import type { NopLiveResultDto, NopSectionDto } from '@/api/types/consolidatedExposure';
import './consolidatedExposure.css';

type TabKey = 'clients-nop' | 'firm-nop' | 'lp-nop' | 'rt-nop' | 'volume-matching' | 'expected-eod-swap';

const LS = { ccy: 'NopLive:ccyCurrency', automap: 'NopLive:symbolMapping', uid: 'NopLive:byUniqueId' };
const readBool = (k: string) => localStorage.getItem(k) === 'true';

export function ConsolidatedExposurePage() {
  const toast = useToast();
  const can = usePermission();

  const [draft, setDraft] = useState<NopFilterState>(() => ({
    ...defaultNopFilters(),
    ccyCurrency: readBool(LS.ccy),
    symbolMapping: readBool(LS.automap),
    byUniqueId: readBool(LS.uid),
  }));
  const [applied, setApplied] = useState<NopFilterState>(draft);
  const [live, setLive] = useState<NopLiveResultDto | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [thresholdsOpen, setThresholdsOpen] = useState(false);

  const onData = useCallback((data: NopLiveResultDto) => {
    setLive(data);
    setLastUpdated(new Date());
  }, []);
  const onError = useCallback((m: string) => toast.error(m), [toast]);

  const { updateFilters } = useNopLive({
    enabled: true,
    initialFilters: toNopFilters(applied),
    onData,
    onError,
  });

  useEffect(() => {
    updateFilters(toNopFilters(applied));
  }, [applied, updateFilters]);

  // Double-click a child Unique ID / Login span -> switch the drill mode to match
  // the clicked cell and refetch, mirroring legacy nop-child-rows.js dblclick:
  //  - Unique ID: force By-Unique-ID on, set the Unique ID filter.
  //  - Login: force By-Unique-ID off, set the Login filter + the connector (unless
  //    Auto Map is on).
  const onDrilldown = (by: 'uniqueId' | 'login', value: string, platformId: number | null) => {
    setDraft((f) => {
      const next: NopFilterState =
        by === 'uniqueId'
          ? { ...f, byUniqueId: true, uniqueId: value, login: null }
          : {
              ...f,
              byUniqueId: false,
              login: Number(value),
              platformId: f.symbolMapping ? f.platformId : platformId ?? f.platformId,
            };
      if (by === 'uniqueId') localStorage.setItem(LS.uid, 'true');
      else localStorage.setItem(LS.uid, 'false');
      setApplied(next);
      return next;
    });
  };

  const fetch = () => setApplied({ ...draft });
  const clear = () => {
    const d = defaultNopFilters();
    setDraft(d);
    setApplied(d);
    localStorage.removeItem(LS.ccy);
    localStorage.removeItem(LS.automap);
    localStorage.removeItem(LS.uid);
  };

  // Mode toggles (Auto Map / By Unique ID / CCY) apply immediately + persist.
  const onModeToggle = (key: 'symbolMapping' | 'byUniqueId' | 'ccyCurrency', value: boolean) => {
    const lsKey = key === 'ccyCurrency' ? LS.ccy : key === 'symbolMapping' ? LS.automap : LS.uid;
    localStorage.setItem(lsKey, String(value));
    setDraft((f) => ({ ...f, [key]: value }));
    setApplied((f) => ({ ...f, [key]: value }));
  };

  // Permission-gated tabs; default to the first the user can see.
  const tabs = useMemo(
    () =>
      (
        [
          { key: 'clients-nop', title: 'Clients NOP', perm: P.clientsNopTab },
          { key: 'firm-nop', title: 'Firm NOP', perm: P.firmNopTab },
          { key: 'lp-nop', title: 'LP NOP', perm: P.lpNopTab },
          { key: 'rt-nop', title: 'R&T NOP', perm: P.rtNopTab },
          { key: 'volume-matching', title: 'Volume Matching', perm: P.volumeMatchingTab },
          { key: 'expected-eod-swap', title: 'Expected EOD Swap', perm: P.expectedEodSwapTab },
        ] as { key: TabKey; title: string; perm: string }[]
      ).filter((t) => can(t.perm)),
    [can],
  );
  const [activeTab, setActiveTab] = useState<TabKey>('clients-nop');
  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.key === activeTab)) setActiveTab(tabs[0].key);
  }, [tabs, activeTab]);

  const isCcy = applied.ccyCurrency;
  void lastUpdated;
  const section = (key: keyof NopLiveResultDto): NopSectionDto | null =>
    (live ? (live[key] as NopSectionDto) : null) ?? null;

  return (
    <div className="consolidated-exposure-page">
      <PageHeader title="Net Open Position" breadcrumb={['Home', 'Consolidated Exposure']} />

      <NopFilterBar
        draft={draft}
        setDraft={setDraft}
        onFetch={fetch}
        onClear={clear}
        onModeToggle={onModeToggle}
        onOpenThresholds={() => setThresholdsOpen(true)}
        canAutoMap={can(P.autoMap)}
        canByUniqueId={can(P.byUniqueId)}
        canByCcy={can(P.byCcy)}
        canThresholds={can(P.nopThresholds)}
      />

      <NopNotifications live={live} />

      {/* Tabs live in the same card as the table (legacy layout). */}
      <div className="card custom-card">
        <div className="card-header p-0 border-bottom">
          <Tabs
            variant="underline"
            tabs={tabs.map((t) => ({ key: t.key, title: t.title }))}
            active={activeTab}
            onChange={(k) => setActiveTab(k as TabKey)}
          />
        </div>
        <div className="card-body p-0">
          {activeTab === 'clients-nop' && <NopSectionTable section={section(isCcy ? 'clientsNopCcy' : 'clientsNop')} isCcy={isCcy} byUniqueId={applied.byUniqueId} card={false} onDrilldown={onDrilldown} />}
          {activeTab === 'firm-nop' && <NopSectionTable section={section(isCcy ? 'firmNopCcy' : 'firmNop')} isCcy={isCcy} byUniqueId={applied.byUniqueId} card={false} onDrilldown={onDrilldown} />}
          {activeTab === 'lp-nop' && <NopSectionTable section={section(isCcy ? 'lpNopCcy' : 'lpNop')} isCcy={isCcy} byUniqueId={applied.byUniqueId} card={false} onDrilldown={onDrilldown} />}
          {activeTab === 'rt-nop' && <NopSectionTable section={section(isCcy ? 'rtNopCcy' : 'rtNop')} isCcy={isCcy} byUniqueId={applied.byUniqueId} card={false} onDrilldown={onDrilldown} />}
          {activeTab === 'volume-matching' && (
            <VolumeMatchingTable items={(isCcy ? live?.volumeMatchingCcy : live?.volumeMatching) ?? []} card={false} />
          )}
          {activeTab === 'expected-eod-swap' && <EodSwapTab filters={toNopFilters(applied)} />}
        </div>
      </div>

      {can(P.nopThresholds) && (
        <NopThresholdsPanel
          open={thresholdsOpen}
          onClose={() => setThresholdsOpen(false)}
          grants={{
            canCreate: can(P.nopThresholdsCreate),
            canEdit: can(P.nopThresholdsEdit),
            canDelete: can(P.nopThresholdsDelete),
          }}
        />
      )}
    </div>
  );
}

function NopNotifications({ live }: { live: NopLiveResultDto | null }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // Matches legacy nop-notifications.js: missing-groups shows the backend text;
  // missing-symbols shows a FIXED message + a "Click To See Symbols" link to
  // Onboarding#symbols (the backend value only toggles visibility).
  const items: { key: string; body: ReactNode }[] = [];
  if (live?.missingGroupsNotification) items.push({ key: `groups:${live.missingGroupsNotification}`, body: live.missingGroupsNotification });
  if (live?.missingSymbolsNotification)
    items.push({
      key: 'missing-symbols',
      body: (
        <>
          <span>We found live trades with symbols that are missing, please add the following symbols to show the live trades:</span>{' '}
          <a href="/User/Onboarding#symbols" className="alert-link">Click To See Symbols</a>
        </>
      ),
    });
  if (live?.disconnectedDataSourcesNotification) items.push({ key: `disc:${live.disconnectedDataSourcesNotification}`, body: live.disconnectedDataSourcesNotification });
  const visible = items.filter((i) => !dismissed.has(i.key));
  if (!visible.length) return null;
  return (
    <div className="mb-2">
      {visible.map((i) => (
        <div key={i.key} className="alert alert-warning alert-dismissible py-2 px-3 mb-1 fs-12 d-flex align-items-center" role="alert">
          <i className="fe fe-alert-triangle me-2" />
          <span className="flex-grow-1">{i.body}</span>
          <button type="button" className="btn-close" aria-label="Dismiss" onClick={() => setDismissed((p) => new Set(p).add(i.key))} />
        </div>
      ))}
    </div>
  );
}
